// appLockStore.js — the per-user app-lock record logic, with NO native/Sentry
// imports so it is unit-testable directly. appLock.js wraps these with the native
// Keychain backend, PBKDF2 hashing, and Sentry reporting.
//
// The record is keyed by Supabase user id, so each account's PIN is isolated on a
// shared phone: a PIN SURVIVES sign-out (same user reuses it), a different user has
// no record under their key (fresh setup), and account deletion removes only that
// user's key. A legacy single-record PIN (pre-keying) is migrated onto its owner's
// key on first read. Nothing here ever touches the network — the PIN hash lives only
// in the device backend, never on a server.

export const SERVER_BASE = 'to.scorify.applock';
export const serverForUser = (userId) => (userId ? `${SERVER_BASE}.${userId}` : SERVER_BASE);
export const RECORD_USER = 'applock';

// The ONLY reasons a record may be wiped (a normal sign-out is NOT one of them —
// the PIN persists). An unsanctioned reason is refused so an unexplained clear can
// never silently delete a PIN.
export const SANCTIONED_CLEAR_REASONS = new Set([
  'account_deletion',
  'reinstall_or_foreign',
  'user_disabled',
  'forgot_pin',
]);

// backend: { get(server) -> {password}|null (async, may throw if missing),
//            set(server, username, password) (async), del(server) (async) }
async function readRaw(backend, server) {
  try {
    const creds = await backend.get(server);
    if (!creds?.password) return null;
    const parsed = JSON.parse(creds.password);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export async function writeLockRecord(backend, userId, state) {
  await backend.set(serverForUser(userId), RECORD_USER, JSON.stringify({ v: 1, ...state }));
}

// Read this user's record. Migrates a legacy single-record PIN onto the owner's key
// the first time they read (and removes the legacy copy so it can't be adopted by a
// different account). Called with no userId only pre-auth, where it reads the legacy
// key for back-compat.
export async function readLockRecord(backend, userId) {
  if (!userId) return readRaw(backend, SERVER_BASE);
  const perUser = await readRaw(backend, serverForUser(userId));
  if (perUser) return perUser;
  const legacy = await readRaw(backend, SERVER_BASE);
  if (legacy && (!legacy.ownerId || legacy.ownerId === userId)) {
    const migrated = { ...legacy, ownerId: userId };
    try { await writeLockRecord(backend, userId, migrated); } catch {}
    try { await backend.del(SERVER_BASE); } catch {}
    return migrated;
  }
  return null;
}

// Delete this user's record. Returns { ok } when done, { refused } for an
// unsanctioned reason (caller reports it; nothing is deleted).
export async function clearLockRecord(backend, reason, userId) {
  if (!SANCTIONED_CLEAR_REASONS.has(reason)) return { refused: true };
  try { await backend.del(serverForUser(userId)); } catch {}
  // On a reinstall/foreign clear (or a pre-auth clear), also drop the legacy
  // base record, which predates per-user keying.
  if (!userId || reason === 'reinstall_or_foreign') {
    try { await backend.del(SERVER_BASE); } catch {}
  }
  return { ok: true };
}
